import React, { useState } from 'react';
import {
  Headphones,
  LineChart,
  Shield,
  CheckCircle,
  Zap,
  Server,
  MessageSquare,
  Brain,
  Ticket,
  Settings,
  Activity,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConsultantChatGate from '../components/ConsultantChatGate';
import TaskCTA from '../components/TaskCTA';
import { trackCbrDiscussTariffClick } from '../lib/metrikaGoals';
import RequestCalculationForm from '../components/RequestCalculationForm';
import FloatingCTA from '../components/FloatingCTA';
import SEOHead from '../components/SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PAGE_SEO } from '../data/pageSeo';
import {
  CBR_TIERS,
  CBR_PAGE,
  CBR_FAQ,
  CBR_SLA_COMPARISON_ROWS,
} from '../data/cbrPricing';

const TIER_ICONS = {
  start: Headphones,
  'ml-analytics': LineChart,
  operations: Settings,
  'cbr-pro': Shield,
};

const START_BLOCK_ICONS = [Ticket, Headphones, Server];
const OPERATIONS_BLOCK_ICONS = [LineChart, Activity, Shield];
const PRO_BLOCK_ICONS = [Zap, Brain, MessageSquare, Server];

const CbrPage = () => {
  const [isCalculationFormOpen, setIsCalculationFormOpen] = useState(false);
  const seo = PAGE_SEO['/cbr'];
  const { hero, tariffs, comparison, startSection, operationsSection, proSection, faq } = CBR_PAGE;

  const openDiscussTariffForm = () => {
    trackCbrDiscussTariffClick();
    setIsCalculationFormOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
      />

      <Header />

      <main className="flex-1 pt-16">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-accent/5 via-transparent to-transparent" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 relative">
            <p className="text-sm font-medium text-accent mb-3">{hero.eyebrow}</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground max-w-3xl">
              {hero.title}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-4">
              {hero.lead}
            </p>
            <p className="text-base text-muted-foreground max-w-2xl leading-relaxed">
              {hero.sub}
            </p>
            <div className="mt-8">
              <Button
                className="bg-accent hover:bg-accent/90 text-accent-foreground h-11 px-6"
                onClick={openDiscussTariffForm}
              >
                {hero.ctaPrimary}
              </Button>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">{tariffs.title}</h2>
          <p className="text-muted-foreground max-w-3xl mb-10">{tariffs.intro}</p>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
            {CBR_TIERS.map((tier) => {
              const Icon = TIER_ICONS[tier.id] || Headphones;
              return (
                <Card
                  key={tier.id}
                  className={
                    tier.highlight
                      ? 'border-accent/50 shadow-lg ring-1 ring-accent/20'
                      : 'border-border/50'
                  }
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-accent" aria-hidden />
                      </div>
                      {tier.highlight && (
                        <span className="text-xs font-medium text-accent bg-accent/10 px-2 py-1 rounded-full">
                          {tariffs.enterpriseBadge}
                        </span>
                      )}
                    </div>
                    <CardTitle className="text-xl">{tier.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">{tier.tagline}</p>
                    <p className="text-3xl font-bold pt-2">
                      {tier.priceLabel}
                      <span className="text-base font-normal text-muted-foreground"> ₽/мес</span>
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">{tier.forWhom}</p>
                    <ul className="space-y-2">
                      {tier.includes.map((item) => (
                        <li key={item} className="flex gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-accent shrink-0 mt-0.5" aria-hidden />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant={tier.highlight ? 'default' : 'outline'}
                      className={
                        tier.highlight
                          ? 'w-full bg-accent hover:bg-accent/90 text-accent-foreground'
                          : 'w-full'
                      }
                      onClick={openDiscussTariffForm}
                    >
                      {tariffs.ctaTemplate} {tier.name}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-6 max-w-3xl">{tariffs.footnote}</p>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">{comparison.title}</h2>
          <p className="text-muted-foreground max-w-2xl mb-6">{comparison.intro}</p>
          <div className="overflow-x-auto rounded-xl border border-border/50">
            <table className="w-full text-sm text-left min-w-[640px]">
              <thead className="bg-muted/50">
                <tr>
                  {comparison.headers.map((h) => (
                    <th key={h} className="px-4 py-3 font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CBR_SLA_COMPARISON_ROWS.map((row) => (
                  <tr key={row.tierId} className="border-t border-border/50">
                    <td className="px-4 py-3 font-medium">{row.name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{row.priceLabel} ₽</td>
                    <td className="px-4 py-3">{row.consult}</td>
                    <td className="px-4 py-3">{row.ticket}</td>
                    <td className="px-4 py-3 text-muted-foreground">{row.monitoring}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">{startSection.title}</h2>
          <p className="text-muted-foreground max-w-2xl mb-8 leading-relaxed">{startSection.intro}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {startSection.blocks.map(({ title, text }, i) => {
              const ItemIcon = START_BLOCK_ICONS[i] || Ticket;
              return (
                <div key={title} className="bg-card rounded-xl border border-border/50 p-6">
                  <ItemIcon className="w-8 h-8 text-accent mb-3" aria-hidden />
                  <h3 className="font-semibold mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-border/30">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">{operationsSection.title}</h2>
          <p className="text-muted-foreground max-w-2xl mb-8 leading-relaxed">{operationsSection.intro}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {operationsSection.blocks.map(({ title, text }, i) => {
              const ItemIcon = OPERATIONS_BLOCK_ICONS[i] || Settings;
              return (
                <div key={title} className="bg-card rounded-xl border border-border/50 p-6">
                  <ItemIcon className="w-8 h-8 text-accent mb-3" aria-hidden />
                  <h3 className="font-semibold mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-muted/30 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-8">{proSection.title}</h2>
            <p className="text-muted-foreground max-w-2xl mb-8 leading-relaxed">{proSection.intro}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {proSection.blocks.map(({ title, text }, i) => {
                const ItemIcon = PRO_BLOCK_ICONS[i] || Zap;
                return (
                  <div key={title} className="bg-card rounded-xl border border-border/50 p-6">
                    <ItemIcon className="w-8 h-8 text-accent mb-3" aria-hidden />
                    <h3 className="font-semibold mb-2">{title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-2xl font-bold mb-6">{faq.title}</h2>
          <div className="space-y-6">
            {CBR_FAQ.map(({ q, a }) => (
              <div key={q}>
                <h3 className="font-semibold mb-2">{q}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </section>

        <TaskCTA
          onCalculationClick={openDiscussTariffForm}
          showRequestButton={false}
        />
      </main>

      <Footer />
      <ConsultantChatGate />
      <FloatingCTA onClick={() => setIsCalculationFormOpen(true)} />
      <RequestCalculationForm
        open={isCalculationFormOpen}
        onOpenChange={setIsCalculationFormOpen}
        calculatorData={null}
      />
    </div>
  );
};

export default CbrPage;
